import { DateTimeFormatter, LocalDate } from '@js-joda/core';
import type { Instrument } from '@thinkalpha/common/schemas/instruments.js';
import type { InstrumentFormState } from '@thinkalpha/ui-types/types/instrument';

const ALL_UNDERSCORES = /_/g;
export const DATE_FORMATTER = DateTimeFormatter.ofPattern('yyMMdd');

// OCC option symbol format: 6 chars (symbol) + 6 digits (yyMMdd) + C/P + 8 digits (strike)
const OPTION_SYMBOL_PATTERN = /^.{6}\d{6}[CP]\d{8}$/;

/**
 * Returns true if the given string looks like an OCC option symbol (21 chars matching the expected format).
 * Use this instead of a bare `length === 21` check to avoid false positives on non-option strings.
 */
export function isOptionInstrumentString(value: string): boolean {
    return value.length === 21 && OPTION_SYMBOL_PATTERN.test(value);
}

function isOnlyDigits(value: string) {
    return /^\d+$/.test(value);
}

/**
 * Given an instrument we will return the underlying symbol
 *
 * Will throw if the instrument is not a valid instrument
 *
 * @param instrument Instrument
 * @returns string
 */
export const getUnderlyingSymbolFromInstrument = (instrument: Instrument | undefined): string => {
    if (!instrument) return '';

    if (instrument.type === 'equity') return instrument.symbol;

    if (instrument.underlyingInstrument.type !== 'equity') {
        throw new Error('UnderlyingInstrument is not an equity');
    }

    return instrument.underlyingInstrument.symbol.slice(0, 6).replace(ALL_UNDERSCORES, '');
};

/**
 * Given an Instrument string we will return the underlying symbol
 *
 * @param symbolString string
 * @returns string
 */
export const getUnderlyingSymbolFromInstrumentString = (symbolString: string): string => {
    if (symbolString.length <= 6) return symbolString;

    return symbolString.slice(0, 6).replace(ALL_UNDERSCORES, '');
};

/**
 * Given an instrument we will return a string representation of the instrument
 *
 * Will throw if the instrument is not a valid instrument
 *
 * @param instrument Instrument
 * @returns string
 */
export const instrumentToString = (instrument: Instrument | undefined): string => {
    if (!instrument) return '';

    if (instrument.type === 'equity') {
        return instrument.symbol;
    }

    if (instrument.underlyingInstrument.type !== 'equity') {
        throw new Error('Instrument underlyingInstrument is not an equity', { cause: instrument });
    }

    if (instrument.strike === undefined || Number.isNaN(Number(instrument.strike))) {
        throw new Error('Instrument Strike is undefined or invalid', { cause: instrument });
    }

    const paddedSymbol = instrument.underlyingInstrument.symbol.padEnd(6, '_');
    const formattedExpiry = (
        typeof instrument.expiry === 'string' ? LocalDate.parse(instrument.expiry) : instrument.expiry
    ).format(DateTimeFormatter.ofPattern('yyMMdd')); // PLAT-10449
    const instrumentRight = instrument.right === 'call' ? 'C' : 'P';

    // Convert to 3 decimal places, then to integer (multiply by 1000)
    const priceInt = Math.round(instrument.strike * 1000);
    // Convert to string and pad with zeros to 8 characters
    const instrumentStrike = priceInt.toString().padStart(8, '0');

    return `${paddedSymbol}${formattedExpiry}${instrumentRight}${instrumentStrike}`;
};

/**
 * Given a Instrument | string we will return an Instrument
 *
 * Will attempt to parse a string into an instrument
 *
 * Will throw if the string is not a valid instrument
 *
 * Will return the instrument if it is already an instrument
 *
 * @param instrument Instrument | string
 * @returns Instrument
 */
export function parseInstrument(instrument: Instrument | string): Instrument {
    if (typeof instrument === 'object') return instrument;

    if (instrument.length === 21) {
        const underlyingSymbol = instrument.slice(0, 6).replace(ALL_UNDERSCORES, '');
        const expirationDateString = instrument.slice(6, 12);
        const expiration = LocalDate.parse(expirationDateString, DATE_FORMATTER);
        const typeString = instrument[12];
        const type = typeString === 'C' ? 'call' : typeString === 'P' ? 'put' : null;
        if (!type) {
            throw new Error(`Invalid option type in symbol: ${instrument}`);
        }
        const strikeStr = instrument.slice(13, 21);
        if (!isOnlyDigits(strikeStr)) {
            throw new Error(`Invalid strike in symbol: ${instrument}`);
        }
        let strike = Number.parseInt(strikeStr, 10);
        strike /= 1000;

        return {
            type: 'option',
            underlyingInstrument: { type: 'equity', symbol: underlyingSymbol },
            strike,
            expiry: expiration,
            right: type,
        };
    }

    return { type: 'equity', symbol: instrument };
}

// Re-export these types from ui-types for backward compatibility
export type { InstrumentFormState } from '@thinkalpha/ui-types/types/instrument';

/**
 * Given an instrument | string we will return an instrument  of type InstrumentFormState
 * that is compatible with the form state
 *
 * "form state" at this time means the expiry is not a LocalDate but a string
 *
 * expiry goes "over the wire" and through schema validation as a string
 *
 * Prefer parseInstrument() in most cases; this helper exists for form validation only.
 *
 * @param instrument Instrument | string
 * @returns InstrumentFormState
 */
export function parseInstrumentToFormState(instrument: Instrument | string): InstrumentFormState {
    const parsedInstrument = parseInstrument(instrument);
    if (parsedInstrument.type === 'option') {
        return {
            ...parsedInstrument,
            expiry:
                typeof parsedInstrument.expiry === 'string'
                    ? parsedInstrument.expiry
                    : parsedInstrument.expiry instanceof LocalDate
                      ? parsedInstrument.expiry.toString()
                      : String(parsedInstrument.expiry),
        };
    }
    return parsedInstrument;
}

export function isValidInstrument(instrument: Instrument): boolean {
    let isValid = false;

    try {
        const instrumentString = instrumentToString(instrument);
        isValid = instrumentString.length > 0;
    } catch (_error) {
        isValid = false;
    }

    return isValid;
}

/**
 * Converts an instrument with string expiry (as used in SendOrderData for JSON serialization)
 * to a string representation.
 *
 * This is a type-safe wrapper around instrumentToString that accepts instruments where
 * the expiry may be a string instead of LocalDate.
 *
 * @param instrument Instrument with string or LocalDate expiry
 * @returns string representation of the instrument
 */
export function instrumentWithStringExpiryToString(
    instrument:
        | { type: 'equity'; symbol: string }
        | {
              type: 'option';
              underlyingInstrument: { type: 'equity'; symbol: string };
              strike: number;
              expiry: string | LocalDate;
              right: 'put' | 'call';
          }
        | undefined,
): string {
    if (!instrument) return '';

    if (instrument.type === 'equity') {
        return instrumentToString(instrument);
    }

    // For options, convert string expiry to LocalDate if needed
    const expiry = typeof instrument.expiry === 'string' ? LocalDate.parse(instrument.expiry) : instrument.expiry;

    return instrumentToString({
        ...instrument,
        expiry,
    });
}
